import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "shared/contexts/AuthContext";
import { startLogin } from "shared/auth/auth";
import { sendGTMEvent } from "shared/services/GTM";
import {
  AlertsApiError,
  createAlert,
} from "shared/services/alerts/alerts.service";
import {
  airportsToBody,
  centsToBRL,
  channelsToFlags,
} from "shared/utils/alerts/alertFormat";
import {
  getAlertEntrySource,
  parseAlertPrefill,
} from "shared/utils/alerts/alertPrefill";
import { getPlatformType } from "shared/utils/platform";
import type { AlertFormValues } from "Components/organisms/AlertForm/types";
import type { GTMEventParams } from "shared/services/GTM/types";
import type { CreateAlertPayload } from "shared/services/alerts/alerts.types";

/** Rota da lista de alertas, destino após salvar ou voltar. */
export const ALERTS_LIST_PATH = "/alertas";

export type CreateAlertFeedback = {
  message: string;
  type: "success" | "error";
};

/**
 * Lógica de criação de alerta compartilhada pelas telas de cadastro da
 * proposta: prefill pela URL, envio à API, telemetria e redirecionamento
 * para a lista com o toast de sucesso.
 */
export const useCreateAlert = () => {
  const { user, refreshSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<CreateAlertFeedback | null>(null);

  const prefill = useMemo(
    () => parseAlertPrefill(location.search),
    [location.search],
  );
  const entrySource = useMemo(() => getAlertEntrySource(prefill), [prefill]);

  const initialValues: AlertFormValues = useMemo(
    () => ({
      origin: prefill.origin,
      destination: prefill.destination,
      openDates: prefill.openDates,
      departureDates: prefill.departureDates,
      returnDates: prefill.returnDates,
      priceCents: prefill.priceCents,
      channels: ["email"],
    }),
    [prefill],
  );

  const trackingFields = (
    values: AlertFormValues,
  ): GTMEventParams["alertCreateSuccess"] => ({
    platform: getPlatformType(),
    source: entrySource,
    IATA_origin: values.origin?.code || "",
    IATA_destination: values.destination?.code || "",
    matchType: values.openDates ? "onlyPrice" : "dateRange",
    departureDates: values.departureDates,
    returnDates: values.returnDates,
    maxPrice: centsToBRL(values.priceCents),
    channel: values.channels.join(","),
  });

  const handleValidationError = (missingFields: string[], message: string) => {
    sendGTMEvent("alertCreateError", {
      ...trackingFields(initialValues),
      errorType: "validation",
      missingFields,
      errorStatus: null,
      errorMessage: message,
    });
  };

  const handleSubmit = async (values: AlertFormValues) => {
    if (!user) return;
    setSaving(true);

    try {
      const payload: CreateAlertPayload = {
        userID: user.id,
        originAirports: airportsToBody([values.origin?.code || ""]),
        destinationAirports: airportsToBody([values.destination?.code || ""]),
        maxPrice: centsToBRL(values.priceCents),
        departureDate: values.openDates ? [] : values.departureDates,
        returnDate: values.openDates ? [] : values.returnDates,
        numberOfAdults: 1,
        numberOfChildren: 0,
        numberOfBabies: 0,
        maxPriceMiles: 300,
        withLuggage: false,
        isStopped: false,
        ...channelsToFlags(values.channels),
        matchType: values.openDates ? "onlyPrice" : "dateRange",
      };

      await createAlert(payload);
      sendGTMEvent("alertCreateSuccess", trackingFields(values));

      navigate(ALERTS_LIST_PATH, {
        replace: true,
        state: {
          feedback: {
            type: "success",
            message:
              "Alerta criado com sucesso! Você receberá uma notificação quando encontrarmos uma passagem.",
          },
        },
      });
    } catch (error) {
      if (error instanceof AlertsApiError && error.status === 401) {
        const refreshed = await refreshSession();
        if (!refreshed) {
          startLogin({ source: "alerts", replaceCurrentPage: true });
          return;
        }
        setFeedback({
          message: "Não foi possível salvar. Tente novamente em instantes.",
          type: "error",
        });
      } else if (error instanceof AlertsApiError) {
        setFeedback({
          message: `Não foi possível salvar. ${error.message}`,
          type: "error",
        });
        sendGTMEvent("alertCreateError", {
          ...trackingFields(values),
          errorType: "api",
          missingFields: [],
          errorStatus: error.status,
          errorMessage: error.message,
        });
      } else {
        setFeedback({
          message: "Não foi possível salvar. Tente novamente em instantes.",
          type: "error",
        });
      }
      setSaving(false);
    }
  };

  return {
    user,
    saving,
    feedback,
    setFeedback,
    initialValues,
    handleSubmit,
    handleValidationError,
  };
};
